import { makeObservable, observable, action, runInAction, reaction } from 'mobx';
import { getRagConfig, testKnowledgeQuery, type KnowledgeSearchHit } from '@/api/rag';
import type { RagConfig } from '@/api/types';
import type { RootStore } from './RootStore';

// Holds merchant-scoped RAG config (Azure AI Search endpoint + models). One row
// per merchant, exposed read-only on the Knowledge → Settings tab. Also caches
// in-flight test-query state so navigating away and back doesn't reset it.

export class RagStore {
  @observable config: RagConfig | null = null;
  @observable isLoadingConfig = false;
  @observable configError: string | null = null;

  // Test query state (per-KB)
  @observable testQuery = '';
  @observable testKnowledgeId: number | null = null;
  @observable testHits: KnowledgeSearchHit[] = [];
  @observable isRunningTest = false;
  @observable testError: string | null = null;
  @observable testRanAt: string | null = null;

  constructor(private root: RootStore) {
    makeObservable(this);

    // Merchant switch invalidates the config (different RAG infra per merchant
    // is supported via per-merchant ApplicationSettings).
    reaction(
      () => this.root.merchantContextStore.selectedMerchantId,
      (merchantId) => {
        if (merchantId != null) {
          void this.fetchConfig();
        } else {
          runInAction(() => {
            this.config = null;
          });
        }
      },
      { fireImmediately: true }
    );
  }

  @action
  async fetchConfig(): Promise<void> {
    this.isLoadingConfig = true;
    this.configError = null;
    try {
      const cfg = await getRagConfig();
      runInAction(() => {
        this.config = cfg;
        this.isLoadingConfig = false;
      });
    } catch (err) {
      runInAction(() => {
        this.isLoadingConfig = false;
        this.configError = err instanceof Error ? err.message : 'Failed to load RAG config';
      });
    }
  }

  @action setTestQuery(v: string): void { this.testQuery = v; }

  @action
  resetTest(knowledgeId: number): void {
    if (this.testKnowledgeId !== knowledgeId) {
      this.testQuery = '';
      this.testHits = [];
      this.testError = null;
      this.testRanAt = null;
      this.testKnowledgeId = knowledgeId;
    }
  }

  @action
  async runTest(knowledgeId: number, query: string): Promise<void> {
    if (!query.trim()) return;
    this.isRunningTest = true;
    this.testError = null;
    this.testKnowledgeId = knowledgeId;
    try {
      const hits = await testKnowledgeQuery({ knowledgeId, query, k: 5 });
      runInAction(() => {
        this.testHits = hits;
        this.testRanAt = new Date().toISOString();
        this.isRunningTest = false;
      });
    } catch (err) {
      runInAction(() => {
        this.isRunningTest = false;
        this.testHits = [];
        this.testError = err instanceof Error ? err.message : 'Test query failed';
      });
    }
  }
}
